import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth'
import { auth } from '../../firebaseConfig'
import Swal from 'sweetalert2'
import { useUser } from '../../context/UserContext'
import { getFirebaseErrorMessage } from '../../utils/errorMessages'

import UserNav from '../../components/navbar/user-nav'
import AdminSidebar from '../../components/admin-sidebar'
import FooterTop from '../../components/footer-top'
import Footer from '../../components/footer'

export default function ChangePassword() {
    const [show, setShow] = useState<boolean>(false)
    const [oldPassword, setOldPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const { user } = useUser()

    // Estados para mostrar/ocultar contraseñas
    const [showOldPassword, setShowOldPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    // Función para evaluar fortaleza de contraseña
    const evaluarFortaleza = (password: string) => {
        if (password.length === 0) return { nivel: 0, texto: '', color: '' }
        if (password.length < 6) return { nivel: 1, texto: 'Muy débil', color: 'danger' }
        if (password.length < 8) return { nivel: 2, texto: 'Débil', color: 'warning' }
        
        let puntos = 0
        if (/[a-z]/.test(password)) puntos++
        if (/[A-Z]/.test(password)) puntos++
        if (/\d/.test(password)) puntos++
        if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) puntos++

        if (puntos >= 4) return { nivel: 5, texto: 'Muy fuerte', color: 'success' }
        if (puntos >= 3) return { nivel: 4, texto: 'Fuerte', color: 'info' }
        return { nivel: 3, texto: 'Regular', color: 'warning' }
    }

    const fortaleza = evaluarFortaleza(newPassword)

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault()
        
        if (newPassword !== confirmPassword) {
            Swal.fire({
                icon: 'error',
                title: 'Las contraseñas no coinciden',
                text: 'La nueva contraseña y su confirmación deben ser idénticas. Por favor, verifica que hayas escrito la misma contraseña en ambos campos.',
                confirmButtonColor: '#d33'
            })
            return
        }

        // Validaciones de seguridad mejoradas
        if (newPassword.length < 8) {
            Swal.fire({
                icon: 'error',
                title: 'Contraseña muy corta',
                html: `
                    <p>La nueva contraseña debe tener <strong>al menos 8 caracteres</strong>.</p>
                    <p class="text-muted mb-0 mt-2">Tu contraseña actual tiene ${newPassword.length} caractere${newPassword.length === 1 ? '' : 's'}.</p>
                `,
                confirmButtonColor: '#d33'
            })
            return
        }

        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
            const result = await Swal.fire({
                icon: 'warning',
                title: 'La contraseña no cumple los requisitos de seguridad',
                html: `
                    <p class="mb-3">Para proteger tu cuenta, tu contraseña debe incluir:</p>
                    <ul class="text-start">
                        <li class="${/[a-z]/.test(newPassword) ? 'text-success' : 'text-danger'}">
                            ${/[a-z]/.test(newPassword) ? '✓' : '✗'} Al menos una letra minúscula (a-z)
                        </li>
                        <li class="${/[A-Z]/.test(newPassword) ? 'text-success' : 'text-danger'}">
                            ${/[A-Z]/.test(newPassword) ? '✓' : '✗'} Al menos una letra mayúscula (A-Z)
                        </li>
                        <li class="${/\d/.test(newPassword) ? 'text-success' : 'text-danger'}">
                            ${/\d/.test(newPassword) ? '✓' : '✗'} Al menos un número (0-9)
                        </li>
                    </ul>
                    <p class="text-muted mt-3 mb-0"><small>Recomendado: incluir también caracteres especiales (!@#$%)</small></p>
                `,
                showCancelButton: true,
                confirmButtonText: 'Continuar de todas formas',
                cancelButtonText: 'Mejorar mi contraseña',
                confirmButtonColor: '#ffc107',
                cancelButtonColor: '#6c757d'
            })

            if (!result.isConfirmed) {
                return
            }
        }

        if (oldPassword === newPassword) {
            Swal.fire({
                icon: 'warning',
                title: 'Contraseña sin cambios',
                text: 'La nueva contraseña debe ser diferente a tu contraseña actual. Por favor, elige una contraseña nueva y segura.',
                confirmButtonColor: '#ffc107'
            })
            return
        }

        if (!auth.currentUser) {
            Swal.fire({
                icon: 'error',
                title: 'Sesión no encontrada',
                text: 'No se pudo verificar tu sesión. Por favor, cierra sesión y vuelve a iniciar sesión antes de cambiar tu contraseña.',
                confirmButtonColor: '#d33'
            })
            return
        }

        setLoading(true)

        try {
            // Reautenticar usuario con contraseña actual
            const credential = EmailAuthProvider.credential(
                auth.currentUser.email!,
                oldPassword
            )
            await reauthenticateWithCredential(auth.currentUser, credential)

            // Cambiar contraseña
            await updatePassword(auth.currentUser, newPassword)

            Swal.fire({
                icon: 'success',
                title: '¡Contraseña actualizada exitosamente!',
                html: `
                    <p>Tu contraseña ha sido cambiada correctamente.</p>
                    <div class="mt-3 p-3 bg-light rounded">
                        <small class="text-muted">
                            <i class="fa fa-shield-alt me-2"></i>
                            <strong>Consejo de seguridad:</strong> Recuerda no compartir tu contraseña con nadie y cambiarla periódicamente.
                        </small>
                    </div>
                `,
                confirmButtonColor: '#3085d6',
                timer: 5000,
                timerProgressBar: true
            })

            // Limpiar formulario
            setOldPassword('')
            setNewPassword('')
            setConfirmPassword('')

        } catch (error: any) {
            let errorTitle = 'Error al cambiar la contraseña'
            let errorMessage = ''

            switch (error.code) {
                case 'auth/wrong-password':
                case 'auth/invalid-credential':
                case 'auth/invalid-login-credentials':
                    errorTitle = 'Contraseña actual incorrecta'
                    errorMessage = 'La contraseña actual que ingresaste no es correcta. Por favor, veríficala e intenta de nuevo.\n\n💡 Sugerencias:\n• Verifica que no tengas activado el bloqueo de mayúsculas\n• Asegúrate de escribir la contraseña correctamente (puedes usar el ícono del ojo para visualizarla)\n• Si olvidaste tu contraseña, cierra sesión y usa la opción "Olvidé mi contraseña"'

                    // Mostrar mensaje especial
                    Swal.fire({
                        icon: 'error',
                        title: errorTitle,
                        html: `
                            <p class="mb-3">La contraseña actual que ingresaste no es correcta.</p>
                            <div class="alert alert-info text-start mb-0">
                                <strong>💡 Sugerencias:</strong>
                                <ul class="mb-0 mt-2" style="padding-left: 20px;">
                                    <li>Verifica que no tengas activado el bloqueo de mayúsculas</li>
                                    <li>Usa el ícono del ojo (<i class="fa fa-eye"></i>) para visualizar tu contraseña</li>
                                    <li>Si olvidaste tu contraseña, cierra sesión y usa "Olvidé mi contraseña"</li>
                                </ul>
                            </div>
                        `,
                        confirmButtonColor: '#d33',
                        confirmButtonText: 'Entendido'
                    })
                    return // Salir del switch para no mostrar el mensaje genérico al final
                case 'auth/weak-password':
                    errorTitle = 'La nueva contraseña no cumple los requisitos de seguridad'
                    errorMessage = 'La nueva contraseña debe tener al menos 6 caracteres. Te recomendamos usar mínimo 8 caracteres combinando letras mayúsculas, minúsculas, números y caracteres especiales.'
                    break
                case 'auth/requires-recent-login':
                    errorTitle = 'Sesión expirada por seguridad'
                    errorMessage = 'Por tu seguridad, debes volver a iniciar sesión antes de cambiar tu contraseña. Por favor, cierra sesión e inicia sesión nuevamente.'
                    break
                case 'auth/too-many-requests':
                    errorTitle = 'Demasiados intentos fallidos'
                    errorMessage = 'Has intentado cambiar tu contraseña varias veces sin éxito. Por seguridad, espera unos minutos antes de intentar de nuevo.'
                    break
                default:
                    errorMessage = getFirebaseErrorMessage(error)
                    Swal.fire({
                        icon: 'error',
                        title: errorTitle,
                        text: errorMessage,
                        confirmButtonColor: '#d33'
                    })
                    return
            }

            // Si llegamos aquí, mostramos un mensaje específico (ya manejado en los cases anteriores)
            if (errorMessage) {
                Swal.fire({
                    icon: 'error',
                    title: errorTitle,
                    text: errorMessage,
                    confirmButtonColor: '#d33'
                })
            }
        } finally {
            setLoading(false)
        }
    }

  return (
    <>
    <UserNav
        nombreUsuario={user?.nombre}
        nombreInmobiliaria={user?.inmobiliaria_nombre}
    />

    <div className="page-title">
        <div className="container">
            <div className="row">
                <div className="col-lg-12 col-md-12">
                    <h2 className="ipt-title">Cambiar Contraseña</h2>
                    <span className="ipn-subtitle">Actualiza tu contraseña para mantener tu cuenta segura.</span>
                </div>
            </div>
        </div>
    </div>

    <section className="py-5">
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12 col-md-12">
                    <div className="filter_search_opt">
                        <Link to="#" onClick={()=>setShow(!show)} className="btn btn-dark full-width mb-4">Dashboard Navigation<i className="fa-solid fa-bars ms-2"></i></Link>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-lg-3 col-md-12">
                    <AdminSidebar show={show} setShow={setShow}/>
                </div>

                <div className="col-lg-9 col-md-12">
                    <div className="card border-0 rounded-4 p-4 shadow-sm">
                        <form onSubmit={handleChangePassword} className="taller-form-modern">
                            {/* Sección 1: Contraseña Actual */}
                            <div className="card mb-4">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0">1. Verificación de Identidad</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="form-group col-lg-12 col-md-6">
                                            <label>Contraseña Actual <span className="text-danger">*</span></label>
                                            <div className="position-relative">
                                                <input
                                                    type={showOldPassword ? "text" : "password"}
                                                    className="form-control"
                                                    value={oldPassword}
                                                    onChange={(e) => setOldPassword(e.target.value)}
                                                    required
                                                    disabled={loading}
                                                    placeholder="Ingresa tu contraseña actual"
                                                    style={{ paddingRight: '45px' }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn position-absolute border-0"
                                                    onClick={() => setShowOldPassword(!showOldPassword)}
                                                    style={{
                                                        right: '5px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        padding: '0.375rem 0.75rem',
                                                        zIndex: 10,
                                                        background: 'transparent',
                                                        textDecoration: 'none'
                                                    }}
                                                    title={showOldPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                                >
                                                    <i className={`fa ${showOldPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                                                </button>
                                            </div>
                                            <small className="text-muted">
                                                Ingresa tu contraseña actual para verificar tu identidad
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Sección 2: Nueva Contraseña */}
                            <div className="card mb-4">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0">2. Nueva Contraseña</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="form-group col-md-6">
                                            <label>Nueva Contraseña <span className="text-danger">*</span></label>
                                            <div className="position-relative">
                                                <input
                                                    type={showNewPassword ? "text" : "password"}
                                                    className="form-control"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    required
                                                    minLength={8}
                                                    disabled={loading}
                                                    placeholder="Ingresa tu nueva contraseña"
                                                    style={{ paddingRight: '45px' }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn position-absolute border-0"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    style={{
                                                        right: '5px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        padding: '0.375rem 0.75rem',
                                                        zIndex: 10,
                                                        background: 'transparent',
                                                        textDecoration: 'none'
                                                    }}
                                                    title={showNewPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                                >
                                                    <i className={`fa ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                                                </button>
                                            </div>
                                            <small className="text-muted d-block mb-2">
                                                Mínimo 8 caracteres. Incluye mayúsculas, minúsculas y números.
                                            </small>
                                            {newPassword && (
                                                <div className="mt-2">
                                                    <div className="progress" style={{height: '6px'}}>
                                                        <div 
                                                            className={`progress-bar bg-${fortaleza.color}`}
                                                            style={{width: `${(fortaleza.nivel / 5) * 100}%`}}
                                                        ></div>
                                                    </div>
                                                    <small className={`text-${fortaleza.color}`}>
                                                        Fortaleza: {fortaleza.texto}
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Confirmar Nueva Contraseña <span className="text-danger">*</span></label>
                                            <div className="position-relative">
                                                <input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    className={`form-control ${confirmPassword ? (newPassword === confirmPassword ? 'is-valid' : 'is-invalid') : ''}`}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    required
                                                    disabled={loading}
                                                    placeholder="Confirma tu nueva contraseña"
                                                    style={{ paddingRight: '45px' }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn position-absolute border-0"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    style={{
                                                        right: '5px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        padding: '0.375rem 0.75rem',
                                                        zIndex: 10,
                                                        background: 'transparent',
                                                        textDecoration: 'none'
                                                    }}
                                                    title={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                                >
                                                    <i className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                                                </button>
                                            </div>
                                            {confirmPassword && (
                                                <div className="mt-1">
                                                    {newPassword === confirmPassword ? (
                                                        <small className="text-success">
                                                            ✅ Las contraseñas coinciden
                                                        </small>
                                                    ) : (
                                                        <small className="text-danger">
                                                            ❌ Las contraseñas no coinciden
                                                        </small>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Botón de submit */}
                            <div className="text-center mt-4">
                                <button
                                    className="btn btn-primary px-5"
                                    type="submit"
                                    disabled={loading || !oldPassword || !newPassword || !confirmPassword}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Cambiando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-key me-2"></i>
                                            Guardar Cambios
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </section>
    <FooterTop bg="theme-bg"/>
    <Footer/>
    </>
  )
}
